import { useEffect, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { PROJECT_SLUG } from '../catalog/component/ComponentPageContext';

export type ServiceName = {
  title: string;
  kind: string;
  namespace: string;
  name: string;
};

/**
 * The catalog's name for each repository that deploys, keyed by the lower-cased
 * `owner/repo` slug (specs/deployments.md AC-2).
 *
 * Asked once for every component's title and slug, rather than per row: the
 * page lists a week of deploys from a dozen repositories, and one list of the
 * components answers them all.
 *
 * A repository with several components — a monorepo — gets no entry, and its
 * deploys show the repository: which of the components a deploy was for is not
 * something the run says, and naming one of them would claim it did.
 *
 * Until the catalog answers, and if it fails, the map is empty and every row
 * shows its repository. The deploys are the page's subject and come from
 * elsewhere; a catalog that is slow or down must not hold them back.
 */
export function useServiceNames(): Map<string, ServiceName> {
  const catalogApi = useApi(catalogApiRef);
  const [names, setNames] = useState(() => new Map<string, ServiceName>());

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { items } = await catalogApi.getEntities({
          filter: { kind: 'Component' },
          fields: [
            'kind',
            'metadata.name',
            'metadata.namespace',
            'metadata.title',
            'metadata.annotations',
          ],
        });
        const bySlug = new Map<string, ServiceName[]>();
        for (const entity of items) {
          const slug =
            entity.metadata.annotations?.[PROJECT_SLUG]?.toLowerCase();
          if (!slug) continue;
          bySlug.set(slug, [
            ...(bySlug.get(slug) ?? []),
            {
              title: entity.metadata.title ?? entity.metadata.name,
              kind: entity.kind,
              namespace: entity.metadata.namespace ?? 'default',
              name: entity.metadata.name,
            },
          ]);
        }
        const unique = new Map<string, ServiceName>();
        for (const [slug, components] of bySlug) {
          if (components.length === 1) unique.set(slug, components[0]);
        }
        if (active) setNames(unique);
      } catch {
        // Left empty: every row keeps its repository name.
      }
    })();
    return () => {
      active = false;
    };
  }, [catalogApi]);

  return names;
}
